"use client"

import React, { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import { RootState } from '@/redux/store';
import { TOrderImportSchema1, orderImportSchema1 } from '@/lib/types';
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from 'react-redux';
import { setOrderInfo } from '@/redux/slices/OrderSlice';
import { Form } from './Form';
import { Link, useRouter } from '@/navigation';
import { useTranslations } from 'next-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';

const CustomerSelectForm = () => {
    const t = useTranslations('CustSelect');
    const t2 = useTranslations('NavigationButtons');
    const formT = useTranslations("orderImportSchema1");
    const { 
        register, 
        handleSubmit, 
        formState: { errors, isSubmitting },
        setError,
        setValue
     } = useForm<TOrderImportSchema1>({
        resolver: zodResolver(orderImportSchema1(formT))
    });

    const router = useRouter();
    const dispatch = useDispatch();
    const orderInfo = useSelector((state: RootState) => state.order);

    // Initial customerType as null to represent no selection
    const [customerType, setCustomerType] = useState<'private' | 'business' | null>(null);

    useEffect(() => {
        if (orderInfo.orderHeadCustomerType === 'private' || orderInfo.orderHeadCustomerType === 'business') {
            setCustomerType(orderInfo.orderHeadCustomerType);
            setValue('orderHeadCustomerType', orderInfo.orderHeadCustomerType);
        } else {
            setCustomerType(null);  // Set null if not selected
        }
        setValue('orderHeadQuotationId', orderInfo.orderHeadQuotationId || '');
    }, [orderInfo, setValue]);

    const handleCustomerTypeSelection = (type: 'private' | 'business') => {
        setCustomerType(type);
        setValue('orderHeadCustomerType', type);
    }

    const onSubmit = (data: TOrderImportSchema1) => {
        if (customerType === null) {
            setError('orderHeadCustomerType', {
                type: 'manual',
                message: 'Customer type must be selected'
            });
            return;
        }
        
        try {
            const updatedInfo = { 
                ...orderInfo, 
                orderHeadQuotationId: data.orderHeadQuotationId || '',
                orderHeadCustomerType: data.orderHeadCustomerType
            }
            dispatch(setOrderInfo(updatedInfo));
            router.push("/order/contact");
        } catch (err) {
            if (process.env.NODE_ENV === "development") {
                console.log(errors);
            }
            setError("root", {
                message: `An error occurred. Please try again.\n${err}`
            });
        }
    }

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <fieldset className='flex flex-col items-center justify-center my-4 md:my-40 max-w-7xl mx-auto p-6'>
                <legend className='text-6xl text-center'>{t('quotation')}</legend>
                <div className='flex space-x-8 items-center'>
                    <input 
                        autoComplete="off"
                        {...register("orderHeadQuotationId")}
                        type='number' 
                        placeholder={t('quotationId')} 
                        inputMode='numeric'
                        pattern="\d*"
                        className={`border-b border-gray-400 ${errors.orderHeadQuotationId ? 'bg-red-400' : ''} px-4 py-2 rounded text-center text-2xl font-mono dark:bg-gray-700`} 
                    />
                </div>
                {errors.orderHeadQuotationId && (
                    <p className='text-red-500'>{`${errors.orderHeadQuotationId.message}`}</p> 
                )}
                <div className='mb-16 text-gray-500'>{t('refToDoc')}</div>
                <legend className='text-6xl mb-1'>{t('customerType')}</legend>
                <div className='mb-8 text-xl md:text-2xl'>{t('whichSuitsYouMost')}</div>
                <div className='flex space-x-8 mb-8'>
                    <button
                        onClick={() => handleCustomerTypeSelection('private')}
                        type="button"
                        className={`group flex flex-col items-center justify-center w-36 h-36 rounded-md text-white text-lg font-bold bg-th-orange hover:bg-th-orange-500  dark:bg-th-orange-600 transition duration-200 ease-in-out ${customerType === 'private' ? 'bg-th-orange-600 dark:bg-th-orange-400' : ''} ${errors.orderHeadCustomerType ? 'border-red-400 border-4' : ''}`}
                    >
                        {t('private')}
                    </button>
                    <button
                        onClick={() => handleCustomerTypeSelection('business')}
                        type="button"
                        className={`group flex flex-col items-center justify-center w-36 h-36 rounded-md text-white text-lg font-bold bg-th-orange hover:bg-th-orange-500  dark:bg-th-orange-600 transition duration-200 ease-in-out ${customerType === 'business' ? 'bg-th-orange-600 dark:bg-th-orange-400' : ''} ${errors.orderHeadCustomerType ? 'border-red-400 border-4' : ''}`}
                    >
                        {t('business')}
                    </button>
                    <input
                        {...register('orderHeadCustomerType')}
                        type='hidden'
                        value={customerType ?? ''}
                    />
                </div>
                {errors.orderHeadCustomerType && (
                    <p className='text-red-500'>{`${errors.orderHeadCustomerType.message}`}</p> 
                )}
                <div className='flex justify-between w-full mt-8'>
                    <Link href="/" className='group bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 py-2 px-5 rounded-md text-lg font-semibold transition duration-400 ease-in-out'>
                        <span className="inline-block transition-transform group-hover:-translate-x-1 motion-reduce:transform-none">
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
                        </span>{t2('back')}
                    </Link>
                    <button
                        disabled={isSubmitting}
                        type="submit"
                        className='group bg-green-500 hover:bg-green-600 text-white py-2 px-5 rounded-md text-lg font-semibold transition duration-400 ease-in-out'>
                        {t2('continue')}
                        <span className="inline-block transition-transform group-hover:translate-x-1 motion-reduce:transform-none">
                            <FontAwesomeIcon icon={faArrowRight} className='ml-1' />
                        </span>
                    </button>
                </div>
                {errors.root && (
                    <p className='text-red-500'>{`${errors.root.message}`}</p>
                )}
            </fieldset>
        </Form>
    );
};

export default CustomerSelectForm;
